import React, { createContext, useState, useContext } from 'react';
import productService from '../services/productService';

const ProductContext = createContext();

export const useProduct = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const openProduct = async (productId) => {
    try {
      setLoading(true);
      setError(null);
      
      // First, try to find the product in cached search results
      const cacheService = await import('../services/cacheService').then(m => m.default);
      const searchCacheKey = cacheService.generateKey('/products/', { limit: 1000 });
      const cachedSearchResults = await cacheService.get(searchCacheKey);
      
      if (cachedSearchResults && cachedSearchResults.data) {
        // Check if product is in the cached search results
        const products = Array.isArray(cachedSearchResults.data) ? cachedSearchResults.data : 
                        cachedSearchResults.data.products || [];
        const cachedProduct = products.find(p => p.id === parseInt(productId));
        
        if (cachedProduct) {
          console.log(`Product ${productId} found in cached search results`);
          setSelectedProduct(cachedProduct);
          setIsOpen(true);
          setLoading(false);
          return;
        }
      }
      
      // If not found in cache, fetch from API
      console.log(`Product ${productId} not in cache, fetching from API`);
      const response = await productService.getProductById(productId);
      setSelectedProduct(response.data);
      setIsOpen(true);
    } catch (err) {
      setError(err.message || 'Error loading product');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const closeProduct = () => {
    setIsOpen(false);
    // Delay clearing product to allow animation
    setTimeout(() => {
      setSelectedProduct(null);
      setError(null);
    }, 300);
  };

  const value = {
    selectedProduct,
    isOpen,
    loading,
    error,
    openProduct,
    closeProduct
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};

export default ProductContext;